import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Play, Pause } from "lucide-react";
import { VIDEO_FILE } from "../lib/content";

const EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];
const AUDIO_SRC = `${process.env.PUBLIC_URL || ""}/videos/${VIDEO_FILE}`;

export default function VideoReel() {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    let unmute = null;
    const attachUnmute = () => {
      unmute = () => {
        if (ref.current) {
          ref.current.muted = false;
          setMuted(false);
          ref.current.play().then(() => setPlaying(true)).catch(() => {});
        }
        EVENTS.forEach((ev) => window.removeEventListener(ev, unmute));
      };
      EVENTS.forEach((ev) => window.addEventListener(ev, unmute, { passive: true }));
    };

    // Try to play immediately WITH sound
    a.muted = false;
    a.play()
      .then(() => {
        setMuted(false);
        setPlaying(true);
      })
      .catch(() => {
        // Browser blocked sound autoplay -> play muted, unmute on first interaction
        a.muted = true;
        setMuted(true);
        a.play().then(() => setPlaying(true)).catch(() => {});
        attachUnmute();
      });

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    return () => {
      if (unmute) EVENTS.forEach((ev) => window.removeEventListener(ev, unmute));
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, []);

  const toggleMute = () => {
    const a = ref.current;
    a.muted = !a.muted;
    setMuted(a.muted);
    if (!a.muted) a.play().catch(() => {});
  };
  const togglePlay = () => {
    const a = ref.current;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  };

  return (
    <>
      {/* Background audio only (video removed) */}
      <audio ref={ref} data-testid="reel-video" src={AUDIO_SRC} loop muted preload="auto" />

      {/* Site-wide floating background music player */}
      <motion.div
        data-testid="bg-player"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.6 }}
        className="fixed bottom-5 left-5 z-[60] flex items-center gap-3 rounded-full border border-[var(--line-gold)] bg-[var(--surface)]/85 backdrop-blur-md px-4 py-2.5 shadow-lg"
      >
        <span className={`eq ${playing && !muted ? "" : "paused"}`}>
          <i /><i /><i /><i />
        </span>
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)] hidden sm:block">
          {playing ? (muted ? "Σε σίγαση" : "Μουσική · Live") : "Μουσική"}
        </span>
        <button
          data-testid="bg-player-mute"
          onClick={toggleMute}
          aria-label={muted ? "Ήχος" : "Σίγαση"}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--fg)] hover:text-[var(--gold-ink)] transition-colors"
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        <button
          data-testid="bg-player-toggle"
          onClick={togglePlay}
          aria-label={playing ? "Στοπ" : "Έναρξη"}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--gold)] text-[#1a1814] hover:bg-[var(--gold-soft)] transition-colors"
        >
          {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
        </button>
      </motion.div>
    </>
  );
}
